import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { InviteStaffFormValues } from './schemas';
import type { MembershipStatus, StaffMember } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

interface StaffListResponse {
  readonly members: readonly StaffMember[];
}

/** `GET /api/v1/merchant/staff` — [PROVISOIRE OQ-14], `@RequireRole('merchant_owner')` (staff.controller.ts). */
export function useStaffQuery(): UseQueryResult<readonly StaffMember[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['staff', activeShopId],
    queryFn: async () => {
      const response = await apiRequest<StaffListResponse>('/merchant/staff', {
        accessToken,
        shopId: activeShopId,
      });
      return response.members;
    },
    enabled: accessToken !== null && activeShopId !== null,
  });
}

/**
 * `POST /merchant/staff` always answers 202, whatever happened
 * (`MembershipRepository.invite()`'s own header: a different response for
 * "no such account" would let an owner discover whether an arbitrary email
 * has an account ANYWHERE on the platform). The caller has no reliable
 * signal from THIS response alone — the query invalidation refetches the
 * real list afterward, which is the only honest way to confirm.
 */
export function useInviteStaffMutation(): UseMutationResult<
  { status: string },
  ApiError,
  InviteStaffFormValues
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: InviteStaffFormValues) =>
      apiRequest<{ status: string }>('/merchant/staff', {
        method: 'POST',
        body: values,
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', activeShopId] });
    },
  });
}

/** What creating or re-issuing returns: the ONLY response that ever carries the provisional password. */
export interface IssuedStaffPassword {
  readonly userId: string;
  readonly temporaryPassword: string;
  readonly expiresAt: string;
  readonly membershipId?: string;
}

/**
 * `POST /merchant/staff/accounts` - the owner creates a team member's account; the server generates a provisional password and returns
 * it ONCE. `gcTime: 0`: the mutation (and the password in its data) is collected at once, and the caller copies what it needs into its
 * own state and calls `reset()` straight away.
 */
export function useCreateStaffAccountMutation(): UseMutationResult<
  IssuedStaffPassword,
  ApiError,
  InviteStaffFormValues
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (values: InviteStaffFormValues) =>
      apiRequest<IssuedStaffPassword>('/merchant/staff/accounts', {
        method: 'POST',
        body: values,
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', activeShopId] });
    },
  });
}

/** `POST /merchant/staff/{id}/temporary-password` - a new provisional password for an account this shop created; a reason is mandatory. */
export function useReissueStaffPasswordMutation(): UseMutationResult<
  IssuedStaffPassword,
  ApiError,
  { membershipId: string; reason: string }
> {
  const { accessToken, activeShopId } = useSession();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ membershipId, reason }) =>
      apiRequest<IssuedStaffPassword>(`/merchant/staff/${membershipId}/temporary-password`, {
        method: 'POST',
        body: { reason },
        accessToken,
        shopId: activeShopId,
      }),
  });
}

export function useUpdatePermissionsMutation(): UseMutationResult<
  StaffMember,
  ApiError,
  { membershipId: string; permissions: readonly string[] }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, permissions }) =>
      apiRequest<StaffMember>(`/merchant/staff/${membershipId}`, {
        method: 'PATCH',
        body: { permissions },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', activeShopId] });
    },
  });
}

export function useSetStaffStatusMutation(): UseMutationResult<
  StaffMember,
  ApiError,
  { membershipId: string; status: MembershipStatus }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, status }) =>
      apiRequest<StaffMember>(`/merchant/staff/${membershipId}`, {
        method: 'PATCH',
        body: { status },
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', activeShopId] });
    },
  });
}

export function useRemoveStaffMutation(): UseMutationResult<null, ApiError, string> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiRequest<null>(`/merchant/staff/${membershipId}`, {
        method: 'DELETE',
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', activeShopId] });
    },
  });
}
